import React, { useState, useMemo } from "react";
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox,
  CheckCircle2, XCircle, ArrowUpRight, ArrowDownRight, Lock,
} from "lucide-react";

/* Radha — Module 4: Revenue Forecasting + Churn/Expansion (self-contained preview)
   Seeded to match 004_forecasts.sql. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";
const formatCr = (n) => `₹${n.toFixed(1)}Cr`;

/* Forecasts (₹ Cr) from 004_forecasts.sql */
const FC = [
  { m: "Jan", bull: 7.8, base: 6.5, bear: 5.4, actual: 6.7 },
  { m: "Feb", bull: 8.2, base: 6.8, bear: 5.6, actual: 6.4 },
  { m: "Mar", bull: 8.5, base: 7.0, bear: 5.8, actual: 7.3 },
  { m: "Apr", bull: 8.8, base: 7.2, bear: 6.0, actual: null },
  { m: "May", bull: 9.0, base: 7.4, bear: 6.1, actual: null },
  { m: "Jun", bull: 8.8, base: 7.1, bear: 5.9, actual: null },
];

const DEALS = [
  { name: "HDFC platform", cust: "HDFC Bank Ltd", stage: "proposal", amt: 30000000, p: 0.55 },
  { name: "Infosys renewal+", cust: "Infosys Ltd", stage: "negotiation", amt: 27000000, p: 0.65 },
  { name: "RIL platform expansion", cust: "Reliance Industries Ltd", stage: "negotiation", amt: 25000000, p: 0.80 },
  { name: "TCS managed services", cust: "Tata Consultancy Services", stage: "proposal", amt: 18000000, p: 0.60 },
  { name: "Bajaj new logo", cust: "Bajaj Finance Ltd", stage: "closed_won", amt: 16000000, p: 1.00 },
  { name: "Wipro expansion", cust: "Wipro Ltd", stage: "qualification", amt: 9000000, p: 0.35 },
  { name: "M&M analytics", cust: "Mahindra & Mahindra", stage: "prospecting", amt: 7000000, p: 0.20 },
].map(d => ({ ...d, weighted: d.amt * d.p }));

const MOVEMENTS = [
  { cust: "Reliance Industries Ltd", type: "expansion", arr: 8000000, p: 0.75 },
  { cust: "Wipro Ltd", type: "expansion", arr: 3000000, p: 0.60 },
  { cust: "Mahindra & Mahindra", type: "contraction", arr: -2500000, p: 0.50 },
  { cust: "HDFC Bank Ltd", type: "churn", arr: -9000000, p: 0.40 },
];

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp, active: true },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 140 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}
function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px" }}>{children}</span>;
}

export default function Forecast() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(false);

  const sums = useMemo(() => ({
    base: FC.reduce((s, x) => s + x.base, 0),
    bull: FC.reduce((s, x) => s + x.bull, 0),
    bear: FC.reduce((s, x) => s + x.bear, 0),
  }), []);
  const weighted = useMemo(() => DEALS.reduce((s, d) => s + d.weighted, 0) / 1e7, []);
  const juneBase = 7.1;
  const coverage = +(weighted / juneBase).toFixed(2);
  const atRiskChurn = useMemo(() =>
    MOVEMENTS.filter(m => m.arr < 0).reduce((s, m) => s + Math.abs(m.arr) * m.p, 0) / 1e7, []);
  const netMovement = useMemo(() => MOVEMENTS.reduce((s, m) => s + m.arr * m.p, 0) / 1e7, []);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M4 · REVENUE FORECASTING + CHURN/EXPANSION</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Forecast & Pipeline Coverage</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Base (6mo)" value={formatCr(sums.base)} sub="bottom-up base case" />
          <KPICard label="Bull / Bear" value={`${formatCr(sums.bull)}`} sub={`bear ${formatCr(sums.bear)}`} accent={C.success} />
          <KPICard label="Weighted Pipeline" value={formatCr(weighted)} sub={`${coverage}× June base`} accent={C.primary} />
          <KPICard label="Net ARR Movement" value={`${netMovement >= 0 ? "+" : ""}${formatCr(netMovement)}`} sub="probability-weighted" accent={netMovement >= 0 ? C.success : C.danger} />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE coverage-gap panel */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · FORECAST RISK</span>
                <span style={{ marginLeft: "auto" }}><Badge color={C.warning}>medium risk</Badge></span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2E" }}>
                June weighted pipeline covers <b>{coverage}×</b> the base forecast, but <b>{formatCr(atRiskChurn)}</b> sits
                in churn/contraction risk — chiefly HDFC (₹90L exposure). Confirm at-risk renewals and keep a buffer before
                publishing June.
              </p>
              <button onClick={() => setDrawer(true)} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review & lock forecast</button>
            </div>

            {/* Scenario chart */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 14px 8px", marginBottom: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 8 }}>Bull / base / bear scenarios vs actual (₹ Cr)</div>
              {view === "loading" ? <Skeleton h={230} /> : view === "error" ? <ErrorState /> : view === "empty" ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={FC} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="band" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.primary} stopOpacity={0.12} />
                        <stop offset="100%" stopColor={C.primary} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke={C.border} vertical={false} />
                    <XAxis dataKey="m" tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                    <YAxis domain={[4, 10]} tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => v == null ? "—" : `₹${v}Cr`} contentStyle={{ borderRadius: 8, border: `0.5px solid ${C.border}`, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: mono }} />
                    <Area type="monotone" dataKey="bull" stroke={C.success} strokeWidth={1} fillOpacity={0} name="Bull" dot={false} />
                    <Area type="monotone" dataKey="bear" stroke={C.warning} strokeWidth={1} fillOpacity={0} name="Bear" dot={false} strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="base" stroke={C.primary} strokeWidth={2.5} name="Base" dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="actual" stroke={C.text} strokeWidth={2.5} name="Actual" dot={{ r: 3 }} connectNulls={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Coverage gauge + pipeline */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Pipeline coverage of June base</span>
                <span style={{ fontFamily: mono, fontSize: 16, fontWeight: 500, color: coverage >= 1 ? C.success : C.danger }}>{coverage}×</span>
              </div>
              <div style={{ height: 8, background: C.border, borderRadius: 4, marginTop: 8, position: "relative", overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, coverage / 1.5 * 100)}%`, height: "100%", background: coverage >= 1 ? C.success : C.danger }} />
                <div style={{ position: "absolute", left: `${1 / 1.5 * 100}%`, top: -2, bottom: -2, width: 2, background: C.text }} title="1.0x" />
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{formatCr(weighted)} weighted pipeline · marker = 1.0× coverage</div>
              <div style={{ marginTop: 12 }}>
                {DEALS.slice(0, 4).map((d, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                    <span style={{ fontSize: 12 }}>{d.cust.split(" ")[0]} · <span style={{ color: C.muted }}>{d.stage.replace("_", " ")}</span></span>
                    <span style={{ fontFamily: mono, fontSize: 11 }}>₹{(d.weighted / 1e7).toFixed(2)}Cr <span style={{ color: C.muted }}>({Math.round(d.p * 100)}%)</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Expansion / churn rail */}
          <div style={{ flex: "0 0 250px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Expansion / churn movements</div>
              {MOVEMENTS.map((m, i) => {
                const pos = m.arr >= 0;
                return (
                  <div key={i} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 500 }}>{m.cust.split(" ")[0]}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontFamily: mono, fontSize: 12, color: pos ? C.success : C.danger }}>
                        {pos ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}₹{Math.abs(m.arr / 1e7).toFixed(1)}Cr
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                      <Badge color={pos ? C.success : C.danger}>{m.type}</Badge>
                      <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{Math.round(m.p * 100)}% likely</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Governance</div>
              {[["Immutable assumptions", "SEBI/board audit trail"], ["bull ≥ base ≥ bear", "enforced at DB layer"], ["weighted = amt × win%", "computed at DB layer"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <LockDrawer onClose={() => setDrawer(false)} coverage={coverage} atRisk={atRiskChurn} />}
    </div>
  );
}

function Skeleton({ h = 200 }) { return <div style={{ padding: 14 }}>{[...Array(4)].map((_, i) => <div key={i} style={{ height: 16, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load forecast</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No forecast for this period</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Create a bull/base/bear forecast to begin.</div></div>; }

function LockDrawer({ onClose, coverage, atRisk }) {
  const [done, setDone] = useState(null);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 400, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Lock June forecast</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          {[["Bear", "₹5.9Cr", C.warning], ["Base", "₹7.1Cr", C.primary], ["Bull", "₹8.8Cr", C.success]].map((x, i) => (
            <div key={i} style={{ flex: 1, background: C.bg, borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", color: x[2] }}>{x[0]}</div>
              <div style={{ fontFamily: mono, fontSize: 16, marginTop: 4 }}>{x[1]}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Assumptions (audited)</div>
        {["RIL platform expansion closes in June at 80% probability (+₹2.0Cr).",
          "HDFC churn risk discounted from base pending CS retention play (−₹36L)."].map((a, i) => (
          <div key={i} style={{ background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "9px 11px", marginTop: 7, fontSize: 12.5, lineHeight: 1.45 }}>{a}</div>
        ))}

        <div style={{ marginTop: 14, background: "#FBF2E0", border: `0.5px solid ${C.warning}33`, borderRadius: 8, padding: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 500, color: C.warning }}>
            <AlertTriangle size={14} /> Medium risk before lock
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5, lineHeight: 1.45 }}>Coverage {coverage}× of base, with ₹{atRisk.toFixed(1)}Cr in churn/contraction risk. Locking writes assumptions to the immutable audit trail.</div>
        </div>

        {done ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: "#E9F7F1", border: `0.5px solid ${C.success}33`, borderRadius: 9, padding: 12, fontSize: 13, color: C.success }}>
            <CheckCircle2 size={15} /> June forecast locked. Assumptions recorded in the audit log.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
            <button onClick={() => setDone(true)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: C.primary, color: "#fff", border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500, cursor: "pointer" }}><Lock size={14} /> Lock forecast</button>
            <button onClick={onClose} style={{ flex: 1, background: "transparent", color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: 9, padding: "11px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
